"use client";

// Подсветка пункта меню переезжает (№34 каталога движения).
//
// Одна пилюля на весь список: она едет к пункту под курсором и возвращается к
// активному, когда мышь ушла. Тот же приём, что у полоски вкладок
// (components/ui/module-tabs.tsx) — списки и бары начинают говорить на одном языке.
//
// Как пользоваться: обернуть список в <MovingHighlight>, пунктам поставить
// data-mh="item" и активному — data-mh-active="1". Подсветка сама найдёт их в
// разметке, поэтому пункты могут быть любыми: кнопки, ссылки, строки.
//
// data-mh-skip="1" — пункт, на который пилюля НЕ заезжает: он уже показывает своё
// состояние сам. Без data-mh-active в списке пилюля показывает только курсор и
// прячется, когда курсор ушёл.

import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
  type CSSProperties, type MutableRefObject, type ReactNode,
} from "react";

type Box = { top: number; height: number };

export function MovingHighlight({
  children,
  className,
  style,
  hostRef: outerHostRef,
}: {
  children: ReactNode;
  className?: string;
  /** Переменные --mh-bg / --mh-border / --mh-shadow перекрашивают пилюлю. */
  style?: CSSProperties;
  /** Хост нужен снаружи (например, для клика мимо списка) — отдаём тот же узел. */
  hostRef?: MutableRefObject<HTMLDivElement | null>;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const pillRef = useRef<HTMLSpanElement | null>(null);
  const hoveredRef = useRef<HTMLElement | null>(null);
  const [animated, setAnimated] = useState(false);

  // ⚠️ ПОДСВЕТКА ДВИГАЕТСЯ МИМО REACT — на нативных событиях и прямой записью
  // в DOM. Ни состояния, ни перерисовок.
  //
  // Через состояние это уже ломалось дважды, каждый раз по-новому: сначала
  // эффект сбрасывал позицию к активному пункту на любом лишнем рендере, потом
  // setState внутри layout-эффекта зациклил обновления, а после — подсветка
  // просто переставала следовать за курсором, потому что перерисовка, от
  // которой зависело перемещение, не происходила (Максим, 26.08.2026).
  // Подсветка ничего не решает в разметке: она только показывает, где курсор.
  // Значит и участвовать в жизненном цикле компонента ей незачем.
  const place = useCallback(() => {
    const host = hostRef.current;
    const el = pillRef.current;
    if (!host || !el) return;
    const hovered = hoveredRef.current?.isConnected ? hoveredRef.current : null;
    const target = hovered ?? host.querySelector<HTMLElement>('[data-mh-active="1"]');
    // Ни курсора, ни активного пункта — прячем совсем, а не оставляем на
    // последнем месте: это врало бы.
    if (!target) { el.style.opacity = "0"; return; }
    const h = host.getBoundingClientRect();
    const r = target.getBoundingClientRect();
    // data-mh-skip — пункт, который УЖЕ показывает своё состояние сам (выбранный
    // отдел подсвечен своим цветом). Пилюля туда ВСЁ РАВНО едет, но по дороге
    // гаснет: два выделения на одной строке — это выделение дважды, а если
    // просто прятать пилюлю заранее, она пропадает не доехав, и движение
    // выглядит оборванным (Максим, 26.08.2026).
    el.style.opacity = target.hasAttribute("data-mh-skip") ? "0" : "1";
    el.style.transform = `translateY(${r.top - h.top + host.scrollTop}px)`;
    el.style.height = `${r.height}px`;
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const onOver = (e: Event) => {
      const item = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-mh="item"]');
      if (!item || !host.contains(item) || item === hoveredRef.current) return;
      hoveredRef.current = item;
      place();
    };
    const onLeave = () => { hoveredRef.current = null; place(); };

    // ⚠️ СЛУШАЕМ И mousemove, не только mouseover.
    // mouseover срабатывает один раз на входе в элемент — и этого достаточно
    // ровно до тех пор, пока под курсором ничего не движется. А список при
    // открытии ВЫЕЗЖАЕТ: пункты едут вверх мимо неподвижного курсора, событие
    // входа при этом не наступает (курсор-то не двигался), и подсветка
    // застревала на том пункте, который оказался под ним первым (Максим,
    // 26.08.2026). mousemove добирает эти случаи; лишней работы нет — при
    // совпадении с текущим пунктом обработчик выходит сразу.
    host.addEventListener("mouseover", onOver);
    host.addEventListener("mousemove", onOver);
    host.addEventListener("mouseleave", onLeave);

    // Пункты приезжают на свои места не сразу — пересчитываем позицию по
    // окончании их выезда, иначе пилюля остаётся там, где пункт был в начале.
    const onAnimEnd = () => place();
    host.addEventListener("animationend", onAnimEnd);

    // Список ещё выезжает, когда его открыли: пункты едут, и позиция, снятая на
    // первом кадре, тут же устареет. ResizeObserver ловит и это, и смену
    // размеров от шрифта или ширины окна.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => place()) : null;
    ro?.observe(host);

    place();
    return () => {
      host.removeEventListener("mouseover", onOver);
      host.removeEventListener("mousemove", onOver);
      host.removeEventListener("mouseleave", onLeave);
      host.removeEventListener("animationend", onAnimEnd);
      ro?.disconnect();
    };
  }, [place]);

  // Первый показ — без переезда: иначе пилюля приезжает от верхнего края,
  // будто пункт только что выбрали.
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      ref={(el) => {
        hostRef.current = el;
        if (outerHostRef) outerHostRef.current = el;
      }}
      className={`mh-host ${className ?? ""}`}
      style={style}
    >
      <span
        ref={pillRef}
        aria-hidden
        className={`mh-pill${animated ? " is-animated" : ""}`}
        style={{ position: "absolute", opacity: 0 }}
      />
      {children}
    </div>
  );
}
